import { CdkDragMove, CdkDragRelease, CdkDragStart } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChildren, ViewContainerRef } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTooltip } from "@angular/material/tooltip";
import { DEFAULT_ERROR_MESSAGE, ScrollService } from "@merri/common";
import { Subject, merge } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { PartnerAccountTypes, PartnerFeatures, SessionService } from "src/app/core";
import { BrandingService } from "src/app/core/services/branding.service";
import { AccountUpgradeDialogService } from "src/app/features/account";
import { DragDropPanelType, VisionboardItem, VisionboardSearchItem } from "../../../models";
import {
  VisionboardCategoriesService,
  VisionboardDragDropService,
  VisionboardItemSearchService,
  VisionboardService,
} from "../../../services";
import { ForceVisionboardItemAddRemoveButton } from "../visionboard-item-add-remove-button/visionboard-item-add-remove-button.component";

@Component({
  selector: "app-visionboard-item-search-results",
  templateUrl: "./visionboard-item-search-results.component.html",
  styleUrls: ["./visionboard-item-search-results.component.scss"],
  standalone: false,
})
export class VisionboardItemSearchResultsComponent implements OnInit, OnDestroy {
  private destroyed$: Subject<void> = new Subject<void>();
  private pageSkipSubject$: Subject<void> = new Subject<void>();

  @Input() disableDrag: boolean;
  @Input() allowDupes: boolean;
  @Input() showUserSelected: boolean;
  @Input() forceVisionboardItemAddRemoveButton: ForceVisionboardItemAddRemoveButton;
  @Input() source: string;
  @Input() itemCustomAddRemoveTemplate: TemplateRef<void>;

  @Output() itemClicked: EventEmitter<VisionboardItem>;
  @Output() paged: EventEmitter<any>;

  @ViewChildren(MatTooltip) tooltips: MatTooltip[];

  showLock: boolean;
  showCustomRequestEmptyState: boolean;
  showCustomUploadSearchResult: boolean;
  pageSize: number;
  lastPageIndex: number = 0;
  itemCount: number;
  items: VisionboardSearchItem[];
  showBottomPaginator: boolean;
  itemToolTipText: string;
  error: string;
  initialLoad: boolean;
  emptyState: boolean;
  placeholderItems: boolean;
  itemDialogOpened: boolean;

  constructor(
    public brandingService: BrandingService,
    private visionboardItemSearchService: VisionboardItemSearchService,
    private visionboardDragDropService: VisionboardDragDropService,
    private visionboardService: VisionboardService,
    private visionboardCategoriesService: VisionboardCategoriesService,
    private sessionService: SessionService,
    private scrollService: ScrollService,
    private matDialog: MatDialog,
    private viewContainerRef: ViewContainerRef,
    private _accountUpgradeDialogService: AccountUpgradeDialogService,
  ) {
    this.allowDupes = true;
    this.showUserSelected = true;
    this.itemClicked = new EventEmitter<VisionboardItem>();
    this.paged = new EventEmitter<any>();
  }

  ngOnInit() {
    this.setState();

    this.pageSkipSubject$.pipe(debounceTime(500), takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.fetchPageInner();
      },
    });

    this.visionboardItemSearchService.currentItems$.pipe(takeUntil(this.destroyed$)).subscribe(result => {
      this.items = result.items;
      this.itemCount = result.count;
      this.error = result.failed ? DEFAULT_ERROR_MESSAGE : null;
      this.initialLoad = result.initialLoad;
      this.showBottomPaginator = this.itemCount > this.pageSize;
      this.emptyState = !this.initialLoad && this.itemCount === 0;
      this.placeholderItems = this.items?.some(i => i.placeholder);
      this.lastPageIndex = result.pageNumber != null ? result.pageNumber : this.lastPageIndex;

      if (this.itemCount === 0) {
        this.lastPageIndex = 0;
      }

      this.setState();
    });

    merge(this.sessionService.sessionUser$, this.visionboardCategoriesService.selectedCategory$)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.setState();
        },
      });

    this.scrollService.scroll$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        if (this.tooltips) {
          this.tooltips.forEach(t => t.hide());
        }
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onPage(pageIndex: number) {
    this.lastPageIndex = pageIndex;

    this.paged.emit();

    if (this.visionboardItemSearchService.cancelLoad()) {
      this.pageSkipSubject$.next();
    } else {
      this.fetchPageInner();
    }
  }

  itemDisabled(item: VisionboardSearchItem): boolean {
    return item.placeholder || (item.isCustomerUploadedSku && this.showLock);
  }

  itemDragDisabled(item: VisionboardSearchItem): boolean {
    return this.disableDrag || this.itemDisabled(item);
  }

  private fetchPageInner(): void {
    this.visionboardItemSearchService.fetchPage(this.lastPageIndex + 1);
  }

  onDragStart(event: CdkDragStart, item: VisionboardSearchItem) {
    this.visionboardDragDropService.onDrag(item, DragDropPanelType.SearchList);
  }

  onDragReleased(event: CdkDragRelease, item: VisionboardSearchItem) {
    this.visionboardDragDropService.onDrop();
  }

  onDragMoved(event: CdkDragMove, item: VisionboardSearchItem) {
    let itemRect = this.visionboardDragDropService.getItemRectFromPreview("visionboard-workspace-item-preview");

    this.visionboardDragDropService.onMove(itemRect);
  }

  onItemClick(item: VisionboardSearchItem) {
    if (item.isCustomerUploadedSku && this.showLock) {
      if (this.sessionService.isExternalPartnerUser()) {
        this._accountUpgradeDialogService.openDialog("Edit Uploaded Item - VB Search", {
          newAccountType: PartnerAccountTypes.AllAccess,
          isFeaturePrompt: true,
          partnerFeature: PartnerFeatures.CustomerVariantSkuManagement,
        });
      }
    } else if (!item.placeholder) {
      this.itemClicked.emit(item.getVisionboardItem());
    }
  }

  onItemDialogOpened(isOpen: boolean): void {
    this.itemDialogOpened = isOpen;
  }

  onCustomRequestClick() {
    this.visionboardService.showItemRequestDialog(
      this.matDialog,
      "VB Request Item in VB Search - Empty State",
      null,
      this.viewContainerRef,
    );
  }

  onAddItemClick() {
    this.visionboardService.showItemRequestDialog(this.matDialog, "VB Request Item in VB Search - Add Button", null, this.viewContainerRef);
  }

  setState(): void {
    this.itemToolTipText = this.disableDrag ? "" : "Drag & Drop";
    this.pageSize = this.visionboardItemSearchService.getPageSize();
    this.showBottomPaginator = this.itemCount > this.pageSize;
    this.showLock = !this.sessionService.isCurrentUserPartnerWithFeature(PartnerFeatures.CustomerVariantSkuManagement);

    this.showCustomRequestEmptyState = this.visionboardItemSearchService.showCustomRequestEmptyState();
    this.showCustomUploadSearchResult = this.visionboardItemSearchService.showCustomUploadSearchResult();
  }
}
